import { type NoyauCode, noyauOf } from '../schemas/noyau.js';
import type { SessionNoyauChoiceReading, SessionNoyauReading } from '../schemas/session-read.js';
import { secondEliminatedOf } from './analysis.js';

/**
 * THE LINE OF THE GAP under « Carte prise » of a first choice (noyau.md §9.1,
 * §9.3, N74, N77; issue #169, planche `EcartSessionParcours`): what the gap
 * was and what the card taken did - the sentence the parcours of Sessions
 * draws with its pill, and the one the markdown export writes.
 *
 * Here and not in either of them, because two ends spell it, at the régime of
 * `NOYAU_CHOICE_BUTTON_LABEL`: three sentences with their figures, written
 * twice, would drift apart the first time one of them is touched. French, like
 * every word the team reads; the operator's words, never the person's - a card
 * never tells the person whether it counted (N73).
 */

/** « 5 points », « 1 point », « 0 point »: the singular below two, as French counts. */
export function noyauPoints(count: number): string {
  return `${count} ${Math.abs(count) < 2 ? 'point' : 'points'}`;
}

/**
 * The four states of the line: the card gave the retained noyau within the
 * threshold, the card changed nothing beyond it, the card changed nothing because
 * the second is a noyau the levels eliminated (analyste.md §8.5, #196), or the
 * first choice was shown before there was a threshold at all.
 */
export type NoyauGapLineState = 'decides' | 'no-effect' | 'second-eliminated' | 'before-threshold';

/** The words of the pill of each state, as the planche writes them. */
export const NOYAU_GAP_BADGE: Readonly<Record<NoyauGapLineState, string>> = {
  decides: 'la carte tranche',
  'no-effect': 'sans effet',
  'second-eliminated': 'sans effet',
  'before-threshold': 'avant le seuil',
};

/** One piece of the sentence: `strong` is what the parcours sets in bold, the figures and the noyau. */
export type NoyauGapLinePart = { readonly text: string; readonly strong: boolean };

export type NoyauGapLine = {
  readonly state: NoyauGapLineState;
  readonly badge: string;
  readonly parts: readonly NoyauGapLinePart[];
};

const plain = (text: string): NoyauGapLinePart => ({ text, strong: false });
const strong = (text: string): NoyauGapLinePart => ({ text, strong: true });

/** « Souverain Magicien (SGP) » - the deposited name, then the triplet. */
function noyauLabel(code: NoyauCode): string {
  return `${noyauOf(code).name} (${code})`;
}

/**
 * The line of ONE choice, or null: on a second choice, before a card is taken
 * - the sentence speaks of « la carte prise » - and on a first choice whose
 * ranking no longer reads, since its figures are the whole of the sentence.
 */
export function noyauGapLineOf(
  noyau: Pick<SessionNoyauReading, 'determination' | 'retained'>,
  choice: Pick<SessionNoyauChoiceReading, 'mode' | 'maxGap' | 'gap' | 'decides'>,
): NoyauGapLine | null {
  const { gap, maxGap, decides } = choice;
  if (choice.mode !== 'first-choice' || decides === null || gap === null) return null;

  // An eliminated noyau is never retained (§8.5, G31): the reason comes before
  // every figure, since neither the gap nor the threshold is what decided.
  const analysed = noyau.determination;
  const second = analysed?.second ?? null;
  if (
    decides === false &&
    second !== null &&
    noyau.retained !== null &&
    secondEliminatedOf(analysed?.analysis ?? null, second)
  ) {
    return {
      state: 'second-eliminated',
      badge: NOYAU_GAP_BADGE['second-eliminated'],
      parts: [
        plain('Le second, '),
        strong(noyauLabel(second)),
        plain(', est éliminé : le principal est retenu, '),
        strong(noyauLabel(noyau.retained)),
        plain('. La carte prise est enregistrée, et ne change rien.'),
      ],
    };
  }
  if (maxGap === null) {
    return {
      state: 'before-threshold',
      badge: NOYAU_GAP_BADGE['before-threshold'],
      parts: [
        plain(
          `Premier choix montré avant l’arrivée du seuil d’écart : la carte prise donne le noyau retenu, quel que soit l’écart (${noyauPoints(gap)} ici).`,
        ),
      ],
    };
  }

  const determination = noyau.determination;
  const scoreOf = (code: NoyauCode | null | undefined): number | null =>
    code === null || code === undefined
      ? null
      : (determination?.scores.find((entry) => entry.noyau === code)?.score ?? null);
  const principalScore = scoreOf(determination?.principal);
  const secondScore = scoreOf(determination?.second);
  const retained = noyau.retained;
  if (principalScore === null || secondScore === null || retained === null) return null;

  const figures = ` entre le principal (${principalScore}) et le second (${secondScore}), `;
  const state: NoyauGapLineState = decides ? 'decides' : 'no-effect';
  return {
    state,
    badge: NOYAU_GAP_BADGE[state],
    parts: decides
      ? [
          plain('Écart de '),
          strong(noyauPoints(gap)),
          plain(`${figures}dans le seuil de ${maxGap} : la carte prise donne le noyau retenu, `),
          strong(noyauLabel(retained)),
          plain('.'),
        ]
      : [
          plain('Écart de '),
          strong(noyauPoints(gap)),
          plain(`${figures}au-delà du seuil de ${maxGap} : le principal est retenu, `),
          strong(noyauLabel(retained)),
          plain('. La carte prise est enregistrée, et ne change rien.'),
        ],
  };
}

/** The sentence of a line in plain text, its parts joined - what the markdown export writes. */
export function noyauGapSentence(line: NoyauGapLine): string {
  return line.parts.map((part) => part.text).join('');
}
